import React, { useCallback, useEffect, useState, useMemo } from 'react';

import Card from './components/AgendaContent/Card';
import EmptyAgenda from './components/AgendaContent/EmptyAgenda';
import Filterbar from './components/AgendaFilters/Filterbar';
import Loader from './components/AgendaContent/Loader';

/**
 * @description		Core functionality for filtering the agenda
 */
const Filter = ({
	config,
	filterFeed,
	availableTags,
	availableCategories,
	rootElement,
}) => {
	const [filteredElements, setFilteredElements] = useState([]); //result of the filtered data
	const [filteredCount, setFilteredCount] = useState(null);
	const [cards, setCards] = useState([]); //state that holds the rendered components
	const [rendering, setRendering] = useState(true);

	//state reflecting params that tweak the filtering
	const [activeTags, setActiveTags] = useState([]);
	const [activeCategories, setActiveCategories] = useState([]);

	// memo's to avoid unnecessary rerender filterbar
	const memoizedFilteredCount = useMemo(() => filteredCount, [filteredCount]);
	const memoizedActiveCategories = useMemo(() => activeCategories, [activeCategories]);
	const memoizedActiveTags = useMemo(() => activeTags, [activeTags]);

	const updateUrl = useCallback(() => {
		const urlParams = new URLSearchParams(); // added the window.location.search so the link is preserved as is
		if (activeTags.length > 0) urlParams.set('f-type', encodeURI(activeTags.map((tag) => Object.keys(tag)).join('+'))); //todo
		if (activeCategories.length > 0) urlParams.set('f-sector', encodeURI(activeCategories.map((category) => Object.keys(category)).join('+')));
		if (urlParams.toString().length > 0) {
			window.history.replaceState(null, '', '?' + urlParams.toString());
		} else {
			window.history.replaceState(null, '', window.location.pathname);
		}
	}, [activeCategories, activeTags]);


	const setTagsParam = useCallback((value, onlyActivate) => {
		const valueKey = Object.keys(value)[0];

		// Als er een actieve categorie is, deselecteer deze
		if (activeCategories.length > 0) {
			 setActiveCategories([]); // Deselecteer actieve categorieën
		}

		// Als de tag al actief is, deselecteer deze dan
		if (activeTags.some((tagOption) => Object.keys(tagOption)[0] === valueKey)) {
			 if (!onlyActivate) {
				  setActiveTags([]);
			 }
		} else {
			 setActiveTags([value]);
		}
  }, [activeTags, activeCategories]);

  const setCategoriesParam = (value) => {
		if (activeTags.length > 0) {
			 setActiveTags([]);
		}

		if (activeCategories.includes(value)) {
			 setActiveCategories([]);
		} else {
			 setActiveCategories([value]);
		}
  };


	const resetParams = () => {
		setActiveCategories([]);
		setActiveTags([]);
	};

	/**
	 * @description	Main filtering functionality, activated by a change in params through useEffect below
	 */
	const filterAccordingToCurrentParams = useCallback(
		(input) => {
			let copyInput = structuredClone(input);

			// Filtering elements based on active tags (OR logic)
			let resultTagFilter = [];
			if (activeTags.length > 0) {
				copyInput.forEach((element) => {
					if (element.type && activeTags.some((tag) => Object.keys(tag)[0] && Object.keys(element.type).includes(Object.keys(tag)[0]))) {
						resultTagFilter.push(element); // Add the element if any tag matches
					}
				});
			} else {
				resultTagFilter = structuredClone(copyInput);
			}

			// Filtering elements based on active categories (OR logic)
			let resultCategoryFilter = [];
			if (activeCategories.length > 0) {
				resultTagFilter.forEach((element) => {
					if (element.sector && activeCategories.some((category) => Object.keys(category)[0] && Object.keys(element.sector).includes(Object.keys(category)[0]))) {
						resultCategoryFilter.push(element); // Add the element if any category matches
					}
				});
			} else {
				resultCategoryFilter = structuredClone(resultTagFilter);
			}

			return resultCategoryFilter;
		},
		[activeCategories, activeTags, config]
	);

	/**
	 * @description	Main filter (reacting to a change in params)
	 */
	useEffect(() => {
		if (!filterFeed) return;

		let filterDataObj = structuredClone(filterFeed);
		const resultFiltering = filterAccordingToCurrentParams(filterDataObj); // Filter the feed according to the active tags and categories
		setFilteredCount(resultFiltering.length);
		setFilteredElements(resultFiltering);
	}, [filterFeed, activeTags, activeCategories, filterAccordingToCurrentParams]);



	/**
	 * @description	Renders the ReactElement cards based on the filteredElements
	 */
	useEffect(() => {
		setRendering(true);

		const cards = filteredElements.map((element, index) => <Card key={index} element={element} setTagsParam={setTagsParam} config={config} />);

		setCards(cards);
		setRendering(false);
	}, [config, filteredElements, setTagsParam]);

	/**
	 * @description	Writes the current parameters to the url
	 */
	useEffect(() => {
		if (!rendering) updateUrl();
	}, [activeTags, activeCategories, rendering, updateUrl]);

	/**
	 * @description	Gets the parameters from url and sets the state accordingly
	 */
	useEffect(() => {
		if (filterFeed && window.location.search) {
			const queryParams = new URLSearchParams(window.location.search);

			if (queryParams.has('f-type')) {
				const slugs = decodeURI(queryParams.get('f-type') ?? '').split('+');
				const params = availableTags.filter((tag) => slugs.includes(Object.keys(tag)[0]));
				if (params.length > 0) setActiveTags(params);
			}

			if (queryParams.has('f-sector')) {
				const slugs = decodeURI(queryParams.get('f-sector') ?? '').split('+');
				const params = availableCategories.filter((category) => slugs.includes(Object.keys(category)[0]));
				if (params.length > 0) setActiveCategories(params);
			}
		}
	}, [filterFeed, availableTags, availableCategories]);

	if (rendering)
		return (
			<div className="loader__container--agenda">
				<Loader />
			</div>
		);

	return (
		<>
			<Filterbar
				memoizedFilteredCount={memoizedFilteredCount}
				memoizedActiveCategories={memoizedActiveCategories}
				memoizedActiveTags={memoizedActiveTags}
				availableCategories={availableCategories}
				availableTags={availableTags}
				setTagsParam={setTagsParam}
				setCategoriesParam={setCategoriesParam}
				resetParams={resetParams}
				config={config}
			/>

			{cards.length < 1 ? (
				<EmptyAgenda text={config.empty_agenda_text} subtext={config.empty_agenda_subtext} />
			) : (
				<ul className={`js_cards-container stories ${!rendering ? 'animated animated__fadeIn' : 'animated animated__preHidden'}`}>{cards}</ul>
			)}
		</>
	);
};

export default React.memo(Filter);
